import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import { or } from 'ember-truth-helpers';
import { on } from '@ember/modifier';
import { concat, fn } from '@ember/helper';
import {
  HdsTable,
  HdsLinkInline,
  HdsCopySnippet,
  HdsIcon,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import { Flex, Time } from 'core/components';
import { rbacRoles, optionForSourceState } from 'core/helpers';

const ListTableV2 = <template>
  <HdsTable
    @caption={{t
      "components.page.access-control.service-principals.list.title"
    }}
    @valign="top"
    data-test-service-principal-rbac-table
  >
    <:head as |H|>
      <H.Tr>
        <H.Th>
          {{t
            "components.page.access-control.service-principals.list.table.headers.name"
          }}
        </H.Th>
        <H.Th @width="27%">
          {{t
            "components.page.access-control.service-principals.list.table.headers.id"
          }}
        </H.Th>
        <H.Th @width="20%">
          {{t
            "components.page.access-control.service-principals.list.table.headers.role"
          }}
        </H.Th>
        <H.Th @width="15%">
          {{t
            "components.page.access-control.service-principals.list.table.headers.scope"
          }}
        </H.Th>
        <H.Th>
          {{t
            "components.page.access-control.service-principals.list.table.headers.created"
          }}
        </H.Th>
        {{#if (can "delete service-principal")}}
          <H.Th>
            {{t
              "components.page.access-control.service-principals.list.table.headers.actions"
            }}
          </H.Th>
        {{/if}}
      </H.Tr>
    </:head>
    <:body as |B|>
      {{#each @servicePrincipalToRoleBindings key="id" as |binding index|}}
        <B.Tr data-test-service-principal-item>
          <B.Td data-test-service-principal-item-cell>
            {{#if (can "get service-principal")}}
              <HdsLinkInline
                class="hds-font-weight-medium"
                @color="secondary"
                @model={{binding.id}}
                @route="cloud.access-control.service-principals.detail"
                data-test-service-principal-item-name-link
              >
                {{binding.name}}
              </HdsLinkInline>
            {{else}}
              <span data-test-service-principal-item-name-label>
                {{binding.name}}
              </span>
            {{/if}}
          </B.Td>
          <B.Td data-test-service-principal-item-cell>
            <HdsCopySnippet @color="secondary" @textToCopy={{binding.id}} />
          </B.Td>
          <B.Td data-test-service-principal-item-cell>
            <Flex @alignItems="start" @direction="column" @gap="xs">
              {{#each binding.roleLocations as |roleLocation|}}
                <span>
                  {{#let
                    (rbacRoles
                      roleLocation.roleId
                      active=true
                      type=roleLocation.link.type
                    )
                    as |rbacRole|
                  }}
                    {{or
                      rbacRole.label
                      roleLocation.title
                      roleLocation.roleId
                      "—"
                    }}
                  {{/let}}
                </span>
              {{else}}
                —
              {{/each}}
            </Flex>
          </B.Td>
          <B.Td data-test-service-principal-item-cell>
            <Flex @alignItems="start" @direction="column" @gap="xs">
              {{#each binding.roleLocations as |roleLocation|}}
                {{#let
                  (optionForSourceState "resource-type" roleLocation.link.type)
                  as |option|
                }}
                  <Flex @alignItems="start" @direction="row" @gap="xs">
                    <span>
                      <HdsIcon @name={{option.icon}} @isInline={{true}} />
                    </span>
                    <span>{{or
                        roleLocation.link.name
                        roleLocation.member.memberId
                        "-"
                      }}</span>
                  </Flex>
                {{/let}}
              {{/each}}
            </Flex>
          </B.Td>
          <B.Td data-test-service-principal-item-cell>
            <Time @date={{binding.createdAt}} />
          </B.Td>
          {{#let
            (can "update service-principal") (can "delete service-principal")
            as |canUpdate canDelete|
          }}
            {{#if (or canUpdate canDelete)}}
              <B.Td data-test-service-principal-item-cell>
                <Flex @justifyContent="end">
                  <HdsDropdown data-test-more-menu as |dd|>
                    <dd.ToggleIcon
                      @icon="more-horizontal"
                      @text={{t "components.form.more"}}
                      @hasChevron={{false}}
                      data-test-more-menu-trigger
                      data-test-service-principal-item-menu
                    />
                    {{#if canUpdate}}
                      <dd.Interactive
                        @icon="edit"
                        @model={{binding.id}}
                        @route="cloud.access-control.service-principals.detail.edit"
                        @text={{t
                          "components.page.access-control.service-principals.list.actions.edit"
                        }}
                        data-test-service-principal-edit
                      />
                    {{/if}}
                    {{#if canDelete}}
                      <dd.Interactive
                        @color="critical"
                        @icon="trash"
                        @text={{t
                          "components.page.access-control.service-principals.list.actions.delete"
                        }}
                        id="modal-dialog-delete-open-button-{{index}}"
                        data-test-delete-service-principals-button
                        {{on
                          "click"
                          (fn
                            @confirmDeleteServicePrincipal.perform
                            binding
                            (concat "modal-dialog-delete-open-button-" index)
                          )
                        }}
                      />
                    {{/if}}
                  </HdsDropdown>
                </Flex>
              </B.Td>
            {{/if}}
          {{/let}}
        </B.Tr>
      {{/each}}
    </:body>
  </HdsTable>
</template>;

export default ListTableV2;
